import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Check, Close, ExpandMore } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { storage } from '../../firebase';
import { makeApiCall, getAuthHeaders } from '../../utils/apiConfig';
import { resolveBrandLogo, getVendorInfo } from '../../utils/vendorLogos';
import {
  BRAND_AUTHORIZATION_LEVELS,
  BrandAuthorization,
  BrandAuthorizationCheckResult,
  BrandAuthorizationLevel,
  KNOWN_DISTRIBUTORS,
  PROOF_MAX_FILE_BYTES,
  checkResultFromRecord,
  isAcceptedProofFileType,
  isCompleteProofLink,
  isValidProofLink,
  levelTakesProof,
  proofStoragePath,
  verificationStatusOf,
} from '../../utils/brandAuthorization';
import { useToast } from '../../contexts/ToastContext';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { ConfirmDialog, StatusChip, StatusTone } from '../ui';
import { tokens } from '../../theme/tokens';
import { brandAuthorizationTagCopy } from './BrandAuthorizationTagChip';
import BrandVerificationCard, { verificationFieldStyles, verifyCardModel } from './BrandVerificationCard';

export const BRAND_AUTHORIZATIONS_QUERY_KEY = ['store-brand-authorizations'] as const;

/**
 * Product default from the MOT-280 handoff: once "Verify now" ran to the end
 * and did not find the store, Save stays blocked until a proof file or https link is attached
 * (the drop zone turns amber and the footer says why). Set to false to let
 * the store save level and channel changes anyway and keep the "Needs proof"
 * badge instead. Open product question; see the MOT-280 PR.
 */
export const BLOCK_SAVE_WHEN_CHECK_FAILS = true;

interface BrandOption {
  id: string;
  name: string;
  logoUrl?: string;
}

interface BrandAuthorizationDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string | null;
  /** Editing an existing record; omit to add a new brand */
  existing?: BrandAuthorization | null;
  brandOptions: BrandOption[];
  brandsLoading?: boolean;
  /** Ids already on the list, hidden from the picker when adding */
  takenBrandIds: string[];
}

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    return body?.message || body?.error?.message || fallback;
  } catch {
    return fallback;
  }
}

/** Thrown when the record itself saved but the proof document failed to
 * upload, so the mutation's onError can show the reason inline instead of
 * a toast, and leave the dialog open with the file still selected. */
class ProofUploadError extends Error {}

const LEVEL_LOCALE_KEYS: Record<BrandAuthorizationLevel, string> = {
  authorized_dealer: 'authorizedDealer',
  reseller: 'reseller',
  special_order: 'specialOrder',
  former_dealer: 'formerDealer',
  unauthorized: 'unauthorized',
};

const { fieldLabelSx, optionalSx, helperSx } = verificationFieldStyles;

const inputSx = {
  borderRadius: '9px',
  fontSize: 14,
  '&.Mui-focused': { boxShadow: `0 0 0 3px ${tokens.accent.tint}` },
} as const;

/**
 * Add or edit one brand authorization from the store shell (Store Settings →
 * Authorized Dealer Associations): the level, the channels, the account the
 * brand (or, for a reseller, the distributor) knows the store by, an on-demand
 * dealer check ("Verify now") and the proof Motzu reviews when that check does
 * not confirm the store. Layout and states follow the MOT-280 design handoff.
 */
const BrandAuthorizationDialog: React.FC<BrandAuthorizationDialogProps> = ({
  open,
  onClose,
  organizationId,
  existing,
  brandOptions,
  brandsLoading,
  takenBrandIds,
}) => {
  const { t } = useAppTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const k = 'store.brandAuthorization.modal';
  const [brand, setBrand] = useState<BrandOption | null>(null);
  const [level, setLevel] = useState<BrandAuthorizationLevel>('authorized_dealer');
  const [online, setOnline] = useState(true);
  const [inStore, setInStore] = useState(true);
  const [accountNumber, setAccountNumber] = useState('');
  const [distributorName, setDistributorName] = useState('');
  const [distributorAccount, setDistributorAccount] = useState('');
  const [notes, setNotes] = useState('');
  const [proofLink, setProofLink] = useState('');
  const [proofNote, setProofNote] = useState('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [proofUploadError, setProofUploadError] = useState<string | null>(null);
  // Once the claim has been written this dialog session (by Save or by
  // "Verify now"), later writes PUT instead of POST so a retry never creates
  // a duplicate.
  const [recordSaved, setRecordSaved] = useState(false);
  // Brand id written first this session. Later writes and proof uploads must
  // keep targeting that claim even if the picker changes.
  const [savedBrandId, setSavedBrandId] = useState<string | null>(null);
  // The record as the latest check left it; supersedes `existing` for status.
  const [checkedRecord, setCheckedRecord] = useState<BrandAuthorization | null>(null);
  const [checkResult, setCheckResult] = useState<BrandAuthorizationCheckResult | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [confirmReverify, setConfirmReverify] = useState<null | 'save' | 'check'>(null);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setBrand({ id: existing.brandId, name: existing.brandName });
      setLevel(existing.level);
      setOnline(existing.channels?.online !== false);
      setInStore(existing.channels?.inStore !== false);
      setAccountNumber(existing.dealerAccountNumber || '');
      setDistributorName(existing.distributorName || '');
      setDistributorAccount(existing.distributorAccountNumber || '');
      setNotes(existing.notes || '');
      setProofLink(existing.proofLink || '');
      setProofNote(existing.proofNote || '');
    } else {
      setBrand(null);
      setLevel('authorized_dealer');
      setOnline(true);
      setInStore(true);
      setAccountNumber('');
      setDistributorName('');
      setDistributorAccount('');
      setNotes('');
      setProofLink('');
      setProofNote('');
    }
    setProofFile(null);
    setFileError(null);
    setProofUploadError(null);
    setRecordSaved(false);
    setSavedBrandId(existing?.brandId || null);
    setCheckedRecord(null);
    setCheckResult(null);
    setCheckError(null);
    setChecking(false);
    setSaveAttempted(false);
    setConfirmReverify(null);
    // A brand Motzu can only verify from proof, or one whose proof was
    // rejected, opens straight onto the manual path.
    const initial = checkResultFromRecord(existing);
    setAdvancedOpen(
      !!existing &&
        levelTakesProof(existing.level) &&
        (existing.verificationPath === 'letter_only' || initial?.outcome === 'rejected'),
    );
  }, [open, existing]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: BRAND_AUTHORIZATIONS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['organization'] });
    queryClient.invalidateQueries({ queryKey: ['availableBrands'] });
    queryClient.invalidateQueries({ queryKey: ['store-organization'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
    // The browser's whole-brand-scope query stays fresh for ten minutes (MOT-628);
    // a brand change moves the store's catalog scope, so it is refetched too.
    queryClient.invalidateQueries({ queryKey: ['storeBrandUniverse'] });
  };

  const current = checkedRecord || existing || null;
  const isReseller = level === 'reseller';
  const takesProof = levelTakesProof(level);
  const proofLinkValid = isValidProofLink(proofLink);
  const brandName = brand?.name || current?.brandName || '';
  const status = current ? verificationStatusOf(current) : null;
  const result = checkResult || checkResultFromRecord(current);
  const path = checkResult?.path || current?.verificationPath || null;
  const model = verifyCardModel({ checking, result, checkError, path, status, isNew: !current });
  const verified = model.state === 'verified';
  // Only a check that ran to the end and did not list the store counts as a
  // failure that needs proof. A lookup that could not run (unavailable) or a
  // request error says so, offers another try and the manual path, and
  // never blocks Save.
  const checkFailed = model.variant === 'not_found';

  const newLink = proofLink.trim() && proofLink.trim() !== (current?.proofLink || '') && isCompleteProofLink(proofLink);
  const hasNewProof = Boolean(proofFile) || Boolean(newLink);
  // A proof already on file counts, unless Motzu rejected it.
  const proofOnFile = Boolean(current?.proof || (current?.proofLink && isCompleteProofLink(current.proofLink))) && status !== 'rejected';
  const hasProof = hasNewProof || proofOnFile;
  // A claim that was verified when the dialog opened is never held back.
  const openedVerified = !!existing && verificationStatusOf(existing) === 'verified';
  const saveBlocked = BLOCK_SAVE_WHEN_CHECK_FAILS && takesProof && checkFailed && !hasProof && !openedVerified;
  const attachedCount = (proofFile || current?.proof ? 1 : 0) + (proofLink.trim() && proofLinkValid ? 1 : 0);
  const submitsProof = takesProof && !verified && hasNewProof;

  const save = useMutation({
    mutationFn: async (): Promise<{ verifiedNow: boolean }> => {
      if (!brand) throw new Error(t(`${k}.errors.brand`));
      if (!online && !inStore) throw new Error(t(`${k}.errors.channel`));
      if (proofFile && fileError) throw new Error(fileError);
      if (takesProof && !proofLinkValid) throw new Error(t(`${k}.link.invalid`));
      const headers = { ...(await getAuthHeaders()), 'Content-Type': 'application/json' };
      const wasVerified = status === 'verified';

      const body = {
        level,
        channels: { online, inStore },
        dealerAccountNumber: accountNumber.trim() || null,
        ...(isReseller
          ? {
              distributorName: distributorName.trim() || null,
              distributorAccountNumber: distributorAccount.trim() || null,
            }
          : {}),
        notes: notes.trim() || null,
        ...(takesProof ? { proofLink: proofLink.trim() || null, proofNote: proofNote.trim() || null } : {}),
      };
      const brandIdForWrite = savedBrandId || brand.id;
      const post = () =>
        makeApiCall('/store/brand-authorizations', {
          method: 'POST',
          headers,
          body: JSON.stringify({ ...body, brandId: brand.id }),
        });
      let response = existing || recordSaved
        ? await makeApiCall(`/store/brand-authorizations/${encodeURIComponent(brandIdForWrite)}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(body),
          })
        : await post();
      // A failed "Verify now" may or may not have created a new brand before
      // it failed, so Save updates it; if it turns out it never was, add it.
      if (!existing && recordSaved && response.status === 404) response = await post();
      if (!response.ok) throw new Error(await readError(response, t(`${k}.errors.save`)));
      setRecordSaved(true);
      setSavedBrandId(brandIdForWrite);
      const payload = await response.json().catch(() => null);
      const saved: BrandAuthorization | null = payload?.data || null;

      if (proofFile) {
        try {
          if (!organizationId) throw new Error('organization not resolved; try again');
          const storagePath = proofStoragePath(organizationId, brandIdForWrite, proofFile.name);
          await uploadBytes(storageRef(storage, storagePath), proofFile, {
            contentType: proofFile.type || 'application/pdf',
          });
          const proofResponse = await makeApiCall(`/store/brand-authorizations/${encodeURIComponent(brandIdForWrite)}/proof`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ fileName: proofFile.name, storagePath, contentType: proofFile.type || undefined }),
          });
          if (!proofResponse.ok) throw new Error(await readError(proofResponse, 'the proof was not recorded'));
        } catch (proofError: any) {
          // The record itself is saved. Keep the dialog open with the file
          // still selected so a retry only has to press Save again.
          invalidate();
          throw new ProofUploadError(
            t(`${k}.errors.proofUpload`, { reason: proofError?.message || 'upload failed' }),
          );
        }
      }
      return { verifiedNow: !wasVerified && !!saved && verificationStatusOf(saved) === 'verified' };
    },
    onSuccess: ({ verifiedNow }) => {
      invalidate();
      showToast(
        verifiedNow
          ? t(`${k}.toast.verified`, { brand: brandName })
          : existing
            ? t(`${k}.toast.updated`)
            : t(`${k}.toast.added`),
        'success',
      );
      onClose();
    },
    onError: (error: any) => {
      if (error instanceof ProofUploadError) {
        setProofUploadError(error.message);
        setAdvancedOpen(true);
        return;
      }
      showToast(error?.message || t(`${k}.errors.save`), 'error');
    },
  });

  // Changing what was verified re-opens the claim server-side; say so first.
  const reopensVerifiedClaim =
    !!current &&
    status === 'verified' &&
    (current.level !== level ||
      (current.channels?.online !== false) !== online ||
      (current.channels?.inStore !== false) !== inStore);

  const runCheck = async () => {
    if (!brand) return;
    if (!online && !inStore) {
      showToast(t(`${k}.errors.channel`), 'error');
      return;
    }
    const brandIdForWrite = savedBrandId || brand.id;
    setChecking(true);
    setCheckError(null);
    try {
      const headers = { ...(await getAuthHeaders()), 'Content-Type': 'application/json' };
      const response = await makeApiCall(`/store/brand-authorizations/${encodeURIComponent(brandIdForWrite)}/check`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          level,
          channels: { online, inStore },
          dealerAccountNumber: accountNumber.trim() || null,
          ...(isReseller
            ? {
                distributorName: distributorName.trim() || null,
                distributorAccountNumber: distributorAccount.trim() || null,
              }
            : {}),
        }),
      });
      if (!response.ok) {
        // The claim is saved before the check runs; a server or network failure
        // after validation can leave it created, so Save must update it.
        if (response.status >= 500) {
          setRecordSaved(true);
          setSavedBrandId(brandIdForWrite);
        }
        throw new Error(await readError(response, t(`${k}.errors.check`)));
      }
      const payload = await response.json();
      const check: BrandAuthorizationCheckResult = payload.check;
      setRecordSaved(true);
      setSavedBrandId(brandIdForWrite);
      setCheckedRecord(payload.data || null);
      setCheckResult(check);
      if (check.outcome === 'verified') setAdvancedOpen(false);
      else if (check.outcome !== 'confirming') setAdvancedOpen(true);
      invalidate();
    } catch (error: any) {
      if (!(error instanceof Error) || error.name === 'TypeError') {
        // No response at all (network): the server may have saved the claim.
        setRecordSaved(true);
        setSavedBrandId(brandIdForWrite);
      }
      setCheckResult(null);
      setCheckError(error?.message || t(`${k}.errors.check`));
      setAdvancedOpen(true);
    } finally {
      setChecking(false);
    }
  };

  const handleCheck = () => {
    if (reopensVerifiedClaim) {
      setConfirmReverify('check');
      return;
    }
    void runCheck();
  };

  const handleSave = () => {
    if (saveBlocked) {
      setSaveAttempted(true);
      setAdvancedOpen(true);
      return;
    }
    if (reopensVerifiedClaim) {
      setConfirmReverify('save');
      return;
    }
    save.mutate();
  };

  const takenBrandKeys = new Set(
    takenBrandIds.map((id) => (id || '').toLowerCase().replace(/[^a-z0-9]/g, '')).filter(Boolean),
  );
  const pickerOptions = brandOptions.filter((b) => {
    const id = (b.id || '').toLowerCase();
    const key = id.replace(/[^a-z0-9]/g, '');
    if (takenBrandIds.some((taken) => (taken || '').toLowerCase() === id)) return false;
    // Hide punctuation variants of the same brand (b_w vs b-w for B+W).
    if (key && takenBrandKeys.has(key)) return false;
    return true;
  });

  const reviewNote = current && status !== 'verified' ? current.verification?.reviewNotes : null;

  const handleFile = (file: File | null) => {
    setProofUploadError(null);
    if (!file) {
      setProofFile(null);
      setFileError(null);
      return;
    }
    if (!isAcceptedProofFileType(file)) {
      setFileError(t('store.brandAuthorization.fileTypeNotAllowed'));
      setProofFile(null);
      return;
    }
    if (file.size > PROOF_MAX_FILE_BYTES) {
      setFileError(t('store.brandAuthorization.fileTooLarge'));
      setProofFile(null);
      return;
    }
    setFileError(null);
    setProofFile(file);
  };

  // Header badge: the in-dialog state first (a level that needs no proof, a
  // check just run), else the record's own tag from MOT-271 with its tooltip.
  const badge: { label: string; tone: StatusTone; help?: string } = (() => {
    if (!takesProof) return { label: t(`${k}.badge.notApplicable`), tone: 'neutral' };
    if (verified) return { label: t(`${k}.badge.verified`), tone: 'positive' };
    if (checkResult && model.variant === 'confirming') {
      return {
        label: t(`store.brandAuthorization.tag.${checkResult.reason === 'partial_name' ? 'listMatchPartial' : 'listMatch'}`, {
          brand: checkResult.listBrandName || brandName,
        }),
        tone: 'info',
      };
    }
    if (status === 'pending') return { label: t(`${k}.badge.inReview`), tone: 'info' };
    if (checkFailed) return { label: t(`${k}.badge.needsProof`), tone: 'caution' };
    const copy = current ? brandAuthorizationTagCopy(t, current) : null;
    if (copy) return { label: copy.label, tone: copy.tone, help: copy.help };
    return { label: t(`${k}.badge.notVerified`), tone: 'neutral' };
  })();

  const logoOption = brandOptions.find((b) => b.id === (brand?.id || current?.brandId));
  const logo = brandName ? resolveBrandLogo(brandName, logoOption?.logoUrl) : null;
  const initials = brandName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');

  const levelMeta = (value: BrandAuthorizationLevel) => ({
    label: t(`${k}.levels.${LEVEL_LOCALE_KEYS[value]}.label`),
    description: t(`${k}.levels.${LEVEL_LOCALE_KEYS[value]}.description`),
  });

  const showChannels = level !== 'former_dealer';
  const showAccount = takesProof;
  const noVerifyNote =
    level === 'former_dealer' ? t(`${k}.noVerify.former`) : level === 'unauthorized' ? t(`${k}.noVerify.unauthorized`) : null;
  const saveLabel = !existing && !recordSaved
    ? submitsProof
      ? t(`${k}.save.addAndSubmit`)
      : t(`${k}.save.add`)
    : submitsProof
      ? t(`${k}.save.submitProof`)
      : t(`${k}.save.changes`);

  const fileMessage = (
    <>
      {proofUploadError && (
        <Alert severity="error" onClose={() => setProofUploadError(null)}>
          {proofUploadError}
        </Alert>
      )}
      {fileError && (
        <Alert severity="error" onClose={() => setFileError(null)}>
          {fileError}
        </Alert>
      )}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="brand-auth-dialog-title"
      PaperProps={{
        sx: {
          maxWidth: 580,
          borderRadius: fullScreen ? 0 : '18px',
          boxShadow: tokens.shadow.overlay,
          maxHeight: fullScreen ? '100%' : 'calc(100vh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 1.5, px: 3, pt: 2.5, pb: 2 }}>
        <Box
          sx={{
            flex: '0 0 auto',
            display: 'grid',
            placeItems: 'center',
            width: 44,
            height: 44,
            border: '1px solid',
            borderColor: tokens.line.default,
            borderRadius: '10px',
            bgcolor: tokens.surface.card,
            overflow: 'hidden',
          }}
        >
          {logo ? (
            <Box component="img" src={logo} alt={t(`${k}.logoAlt`, { brand: brandName })} sx={{ maxWidth: 36, maxHeight: 36, objectFit: 'contain' }} />
          ) : (
            <Typography aria-hidden sx={{ fontSize: 15, fontWeight: 700, color: tokens.ink.bodyAlt }}>
              {initials || '·'}
            </Typography>
          )}
        </Box>
        <Typography
          id="brand-auth-dialog-title"
          component="h2"
          sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em', color: tokens.ink.primary, minWidth: 0 }}
          noWrap
        >
          {existing ? t(`${k}.editTitle`, { brand: existing.brandName }) : brand ? t(`${k}.addTitleBrand`, { brand: brand.name }) : t(`${k}.addTitle`)}
        </Typography>
        {(existing || brand) && (
          <Tooltip title={badge.help || ''} disableHoverListener={!badge.help}>
            <span data-testid="brand-auth-badge">
              <StatusChip label={badge.label} tone={badge.tone} dot size="small" sx={{ height: 24, fontSize: 12, fontWeight: 600, borderRadius: '7px' }} />
            </span>
          </Tooltip>
        )}
        <IconButton
          onClick={onClose}
          aria-label={t(`${k}.close`)}
          sx={{ ml: 'auto', width: 32, height: 32, borderRadius: '8px', color: tokens.ink.muted, '&:hover': { bgcolor: tokens.surface.tint, color: tokens.ink.primary } }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box
        sx={{
          flex: '1 1 auto',
          overflowY: 'auto',
          px: 3,
          pt: 0.5,
          pb: 3,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          alignContent: 'start',
          gridAutoRows: 'max-content',
          gap: '22px',
        }}
      >
        {reviewNote && (
          <Alert severity={status === 'rejected' ? 'error' : status === 'expired' ? 'warning' : 'info'}>
            {t('store.brandAuthorization.reviewNote', { note: reviewNote })}
          </Alert>
        )}

        {!existing && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography component="label" htmlFor="brand-auth-brand" sx={fieldLabelSx}>{t(`${k}.brand`)}</Typography>
            <Autocomplete
              id="brand-auth-brand"
              options={pickerOptions}
              getOptionLabel={(option) => option.name}
              value={brand}
              onChange={(_e, value) => setBrand(value)}
              disabled={!!savedBrandId}
              loading={brandsLoading}
              renderOption={(props, option) => {
                const optionLogo = resolveBrandLogo(option.name, option.logoUrl);
                const info = getVendorInfo(option.name);
                const { key, ...rest } = props as typeof props & { key?: React.Key };
                return (
                  <Box component="li" key={key} {...rest} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      src={optionLogo || undefined}
                      sx={{ width: 28, height: 28, bgcolor: optionLogo ? 'common.white' : info.color, fontSize: 13, color: 'common.white' }}
                      imgProps={{ style: { objectFit: 'contain', padding: 3 } }}
                    >
                      {!optionLogo && option.name.charAt(0)}
                    </Avatar>
                    <Typography>{option.name}</Typography>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t(`${k}.brandPlaceholder`)}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    sx: { ...inputSx, minHeight: 40 },
                    endAdornment: (
                      <>
                        {brandsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}

        {/* 1. Authorization level */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography id="brand-auth-level-label" sx={fieldLabelSx}>{t(`${k}.level`)}</Typography>
          <Select
            value={level}
            onChange={(e) => setLevel(e.target.value as BrandAuthorizationLevel)}
            labelId="brand-auth-level-label"
            IconComponent={ExpandMore}
            renderValue={(value) => {
              const meta = levelMeta(value as BrandAuthorizationLevel);
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: tokens.ink.primary }}>{meta.label}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, whiteSpace: 'normal' }}>{meta.description}</Typography>
                </Box>
              );
            }}
            MenuProps={{
              PaperProps: { sx: { mt: 0.75, borderRadius: '12px', boxShadow: tokens.shadow.flyout, p: 0.75 } },
              MenuListProps: { sx: { p: 0, display: 'flex', flexDirection: 'column', gap: '2px' } },
            }}
            sx={{
              borderRadius: '10px',
              '& .MuiSelect-select': { py: 1.375, px: 1.75 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.ink.disabled },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: tokens.ink.faint },
              '&.Mui-focused': { boxShadow: `0 0 0 3px ${tokens.accent.tint}` },
              '& .MuiSelect-icon': { color: tokens.ink.muted, transition: 'transform .15s' },
            }}
            fullWidth
          >
            {BRAND_AUTHORIZATION_LEVELS.map((option) => {
              const meta = levelMeta(option.value);
              return (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    gap: 1.5,
                    px: 1.25,
                    py: 1.125,
                    borderRadius: '8px',
                    '&.Mui-selected, &.Mui-selected:hover': { bgcolor: tokens.accent.tint },
                    '&:hover': { bgcolor: tokens.surface.tint },
                  }}
                >
                  <Box sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: tokens.ink.primary }}>{meta.label}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, whiteSpace: 'normal' }}>{meta.description}</Typography>
                  </Box>
                  {option.value === level && <Check sx={{ fontSize: 18, color: tokens.accent.main }} />}
                </MenuItem>
              );
            })}
          </Select>
        </Box>

        {/* 2. Where you're authorized to sell */}
        {showChannels && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography sx={fieldLabelSx}>
                {level === 'unauthorized' ? t(`${k}.channels.titleUnauthorized`) : t(`${k}.channels.title`)}
              </Typography>
              <Typography sx={{ ...helperSx, textWrap: 'pretty' }}>
                {level === 'unauthorized' ? t(`${k}.channels.helpUnauthorized`) : t(`${k}.channels.help`)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(
                [
                  ['inStore', inStore, setInStore],
                  ['online', online, setOnline],
                ] as const
              ).map(([key, on, set]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={on}
                      onChange={(e) => set(e.target.checked)}
                      size="small"
                      disableRipple
                      sx={{ p: 0, mr: 1, color: tokens.ink.disabled, '&.Mui-checked': { color: tokens.accent.main } }}
                    />
                  }
                  label={t(`${k}.channels.${key}`)}
                  sx={{
                    m: 0,
                    height: 38,
                    pl: 1.375,
                    pr: 1.75,
                    border: '1px solid',
                    borderColor: on ? tokens.accent.border : tokens.line.default,
                    borderRadius: '9px',
                    bgcolor: on ? tokens.signal.info.row : tokens.surface.card,
                    '& .MuiFormControlLabel-label': { fontSize: 13.5, fontWeight: 600, color: tokens.ink.primary },
                  }}
                />
              ))}
            </Box>
            {!online && !inStore && (
              <Typography sx={{ fontSize: 12.5, color: tokens.signal.caution.ink }}>{t(`${k}.errors.channel`)}</Typography>
            )}
          </Box>
        )}

        {/* 3. Account (optional) */}
        {showAccount &&
          (isReseller ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography component="label" htmlFor="brand-auth-distributor" sx={fieldLabelSx}>
                  {t('store.brandAuthorization.reseller.distributor')}
                </Typography>
                <Autocomplete
                  id="brand-auth-distributor"
                  freeSolo
                  options={[...KNOWN_DISTRIBUTORS]}
                  value={distributorName}
                  inputValue={distributorName}
                  onInputChange={(_e, value) => setDistributorName(value)}
                  onChange={(_e, value) => setDistributorName(typeof value === 'string' ? value : '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      inputProps={{ ...params.inputProps, maxLength: 160 }}
                      InputProps={{ ...params.InputProps, sx: { ...inputSx, minHeight: 40 } }}
                    />
                  )}
                />
                <Typography sx={helperSx}>{t('store.brandAuthorization.reseller.distributorHelp')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography component="label" htmlFor="brand-auth-distributor-account" sx={fieldLabelSx}>
                    {t('store.brandAuthorization.reseller.distributorAccount')}
                  </Typography>
                  <Typography component="span" sx={optionalSx}>{t(`${k}.optional`)}</Typography>
                </Box>
                <TextField
                  id="brand-auth-distributor-account"
                  size="small"
                  value={distributorAccount}
                  onChange={(e) => setDistributorAccount(e.target.value)}
                  placeholder={t(`${k}.account.placeholderReseller`)}
                  inputProps={{ maxLength: 120 }}
                  InputProps={{ sx: { ...inputSx, height: 40 } }}
                  fullWidth
                />
                <Typography sx={{ ...helperSx, textWrap: 'pretty' }}>{t(`${k}.account.helpReseller`)}</Typography>
              </Box>
              {/* A reseller rarely has a brand account; keep one that was entered before so it can be moved or cleared. */}
              {(current?.dealerAccountNumber || accountNumber) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  <Typography component="label" htmlFor="brand-auth-brand-account" sx={fieldLabelSx}>
                    {t('store.brandAuthorization.reseller.brandAccount')}
                  </Typography>
                  <TextField
                    id="brand-auth-brand-account"
                    size="small"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    inputProps={{ maxLength: 120 }}
                    InputProps={{ sx: { ...inputSx, height: 40 } }}
                    fullWidth
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography component="label" htmlFor="brand-auth-account" sx={fieldLabelSx}>
                  {t(`${k}.account.label`)}
                </Typography>
                <Typography component="span" sx={optionalSx}>{t(`${k}.optional`)}</Typography>
              </Box>
              <TextField
                id="brand-auth-account"
                size="small"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                placeholder={t(`${k}.account.placeholder`, { brand: brandName || t(`${k}.thisBrand`) })}
                inputProps={{ maxLength: 120 }}
                InputProps={{ sx: { ...inputSx, height: 40 } }}
                fullWidth
              />
              <Typography sx={{ ...helperSx, textWrap: 'pretty' }}>
                {t(`${k}.account.help`, { brand: brandName || t(`${k}.thisBrand`) })}
              </Typography>
            </Box>
          ))}

        {/* 4. Verification */}
        {takesProof && (
          <>
            <BrandVerificationCard
              brandName={brandName}
              model={model}
              result={result}
              checkError={checkError}
              path={path}
              canCheck={!!brand && (online || inStore) && !save.isPending}
              onCheck={handleCheck}
              advancedOpen={advancedOpen}
              onToggleAdvanced={() => setAdvancedOpen((v) => !v)}
              proofRequired={checkFailed}
              highlightDropZone={saveAttempted && saveBlocked}
              proofFile={proofFile}
              onProofFile={handleFile}
              existingProof={current?.proof || null}
              proofLink={proofLink}
              onProofLink={setProofLink}
              existingProofLink={current?.proofLink || null}
              proofNote={proofNote}
              onProofNote={setProofNote}
              attachedCount={attachedCount}
              fileMessage={fileMessage}
            />
            <Typography sx={{ ...helperSx, mt: '-10px', textWrap: 'pretty' }}>{t(`${k}.unlocks`)}</Typography>
          </>
        )}

        {noVerifyNote && <Typography sx={{ fontSize: 13, color: tokens.ink.muted, textWrap: 'pretty' }}>{noVerifyNote}</Typography>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography component="label" htmlFor="brand-auth-notes" sx={fieldLabelSx}>{t(`${k}.notes`)}</Typography>
            <Typography component="span" sx={optionalSx}>{t(`${k}.optional`)}</Typography>
          </Box>
          <TextField
            id="brand-auth-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            inputProps={{ maxLength: 1000 }}
            InputProps={{ sx: { ...inputSx } }}
            fullWidth
          />
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 3,
          py: 1.75,
          borderTop: '1px solid',
          borderColor: tokens.line.hairline,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Typography
          role="status"
          aria-live="polite"
          data-testid="brand-auth-footer-note"
          sx={{ flex: '1 1 auto', fontSize: 12.5, color: tokens.signal.caution.ink }}
        >
          {saveAttempted && saveBlocked ? t(`${k}.footer.blocked`) : ''}
        </Typography>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            height: 38,
            px: 2,
            borderRadius: '9px',
            fontSize: 13.5,
            fontWeight: 600,
            textTransform: 'none',
            color: tokens.ink.body,
            borderColor: tokens.line.default,
            '&:hover': { borderColor: tokens.ink.disabled, color: tokens.ink.primary, bgcolor: tokens.surface.card },
          }}
        >
          {t(`${k}.cancel`)}
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSave}
          aria-disabled={saveBlocked || undefined}
          disabled={save.isPending || checking || !brand || (takesProof && !proofLinkValid)}
          sx={{
            height: 38,
            px: 2.25,
            borderRadius: '9px',
            fontSize: 13.5,
            fontWeight: 600,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            ...(saveBlocked
              ? { opacity: 0.5, cursor: 'not-allowed', '&:hover': { bgcolor: 'primary.main' } }
              : {}),
          }}
        >
          {save.isPending ? <CircularProgress size={18} color="inherit" /> : saveLabel}
        </Button>
      </Box>

      <ConfirmDialog
        open={confirmReverify !== null}
        title={t('store.brandAuthorization.confirmReverify.title', { brand: brandName })}
        message={t('store.brandAuthorization.confirmReverify.message', { brand: brandName })}
        confirmLabel={
          confirmReverify === 'check'
            ? t(`${k}.confirmReverifyCheck`)
            : t('store.brandAuthorization.confirmReverify.confirm')
        }
        confirmColor="warning"
        loading={save.isPending || checking}
        onConfirm={() => {
          const action = confirmReverify;
          setConfirmReverify(null);
          if (action === 'check') void runCheck();
          else save.mutate();
        }}
        onClose={() => setConfirmReverify(null)}
      />
    </Dialog>
  );
};

export default BrandAuthorizationDialog;
